import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import type { BrandTeamSession } from '../../../../services/brandTeamService';

interface SessionSelectorProps {
  sessions: BrandTeamSession[];
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
}

const statusDot: Record<string, string> = {
  generated: 'bg-green-500',
  generating: 'bg-purple-500 animate-pulse',
  failed: 'bg-red-500',
  planned: 'bg-blue-400',
  brief_approved: 'bg-blue-400',
  plan_approved: 'bg-indigo-500',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const SessionSelector: React.FC<SessionSelectorProps> = ({ sessions, activeSessionId, onSelectSession, onNewSession }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const label = activeSession ? activeSession.name || `Session ${activeSession.id.slice(0, 8)}` : 'New Session';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg border border-primary-100 bg-white px-3 py-2 text-sm font-medium text-text-dark shadow-sm hover:bg-gray-50 transition-colors"
      >
        {activeSession && (
          <span className={`h-2 w-2 rounded-full ${statusDot[activeSession.status] || 'bg-gray-400'}`} />
        )}
        <span className="max-w-[200px] truncate">{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-xl border border-primary-100 bg-white py-1 shadow-xl">
          <button
            type="button"
            onClick={() => { onNewSession(); setOpen(false); }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-semibold text-brand-purple hover:bg-primary-50 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Session
          </button>
          <div className="mx-3 my-1 h-px bg-primary-100" />
          <div className="max-h-60 overflow-y-auto">
            {sessions.map((session) => {
              const isActive = session.id === activeSessionId;
              return (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => { onSelectSession(session.id); setOpen(false); }}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    isActive ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className={`h-2 w-2 flex-shrink-0 rounded-full ${statusDot[session.status] || 'bg-gray-400'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-text-dark truncate">
                      {session.name || `Session ${session.id.slice(0, 8)}`}
                    </div>
                    <div className="text-[10px] text-body-grey">{session.status} &middot; {formatDate(session.createdAt)}</div>
                  </div>
                </button>
              );
            })}
            {sessions.length === 0 && (
              <p className="px-4 py-3 text-xs text-gray-400 text-center">No sessions yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionSelector;
